/**
 * Vordefinierte Seitenvorlagen für häufige UK-Situationen.
 * Jede Vorlage enthält einen Titel, Slot-Labels und Footer-Defaults.
 * Beim Erstellen einer Seite werden die Labels automatisch mit
 * vorhandenen Piktogrammen aus der Bibliothek abgeglichen.
 */

export interface PageTemplate {
  id: string;
  title: string;
  description: string;
  icon: string;
  slotCount: number;
  mainSlotLabels: string[];
  footerLabels: string[];
  group?: string;
}

export const PAGE_TEMPLATES: PageTemplate[] = [
  // ── Basis-Vorlagen ─────────────────────────────────────
  {
    id: 'ich',
    title: 'Über mich',
    description: 'Persönliche Kommunikationsseite – ich, wollen, können …',
    icon: '👤',
    group: 'Basis',
    slotCount: 12,
    mainSlotLabels: [
      'ich', 'du', 'wollen', 'können', 'machen', 'haben',
      'gut', 'gehen', 'Mama', 'Papa', 'Freund', 'Zuhause',
    ],
    footerLabels: ['mehr', 'nicht', 'nochmal', 'fertig', 'Hilfe', 'Pause', 'ja', 'nein'],
  },
  {
    id: 'zuhause',
    title: 'Zuhause',
    description: 'Alltag und Aktivitäten daheim',
    icon: '🏠',
    group: 'Basis',
    slotCount: 12,
    mainSlotLabels: [
      'essen', 'trinken', 'schlafen', 'spielen', 'fernsehen', 'kochen',
      'Toilette', 'duschen', 'draußen', 'Küche', 'Garten', 'Kleidung',
    ],
    footerLabels: ['mehr', 'nicht', 'nochmal', 'fertig', 'Hilfe', 'Pause', 'ja', 'nein'],
  },
  {
    id: 'pflege',
    title: 'Pflege',
    description: 'Körperpflege, Hygiene und Gesundheit',
    icon: '🧼',
    group: 'Basis',
    slotCount: 8,
    mainSlotLabels: [
      'Zähne putzen', 'duschen', 'kämmen', 'Toilette',
      'Kleidung', 'Medizin', 'Windel', 'Schmerzen',
    ],
    footerLabels: ['ja', 'nein', 'fertig', 'Hilfe'],
  },
  {
    id: 'freunde',
    title: 'Freunde',
    description: 'Soziale Kontakte und Zusammensein',
    icon: '👫',
    group: 'Basis',
    slotCount: 8,
    mainSlotLabels: [
      'hallo', 'tschüss', 'spielen', 'sprechen',
      'bitte', 'danke', 'Freund', 'glücklich',
    ],
    footerLabels: ['mehr', 'nicht', 'nochmal', 'fertig', 'ja', 'nein'],
  },
  {
    id: 'arbeit',
    title: 'Arbeit',
    description: 'Beschäftigung und Werkstatt',
    icon: '🔧',
    group: 'Basis',
    slotCount: 6,
    mainSlotLabels: [
      'arbeiten', 'Pause', 'fertig',
      'Hilfe', 'trinken', 'Toilette',
    ],
    footerLabels: ['ja', 'nein', 'mehr', 'gut'],
  },
  {
    id: 'aktivitaeten',
    title: 'Aktivitäten',
    description: 'Freizeit und Beschäftigung',
    icon: '🎨',
    group: 'Basis',
    slotCount: 12,
    mainSlotLabels: [
      'spielen', 'lesen', 'malen', 'Musik', 'turnen', 'basteln',
      'kochen', 'spazieren', 'tanzen', 'singen', 'fernsehen', 'draußen',
    ],
    footerLabels: ['mehr', 'nicht', 'nochmal', 'fertig', 'Hilfe', 'Pause', 'ja', 'nein'],
  },

  // ── Plauderbuch-Vorlagen (nach example-Plauderbuch-01.pdf) ──
  {
    id: 'pb-hallo',
    title: 'Hallo / Ich erzähle',
    description: 'Begrüßung und Gesprächseinstieg – Plauderbuch',
    icon: '👋',
    group: 'Plauderbuch',
    slotCount: 8,
    mainSlotLabels: [
      'hallo', 'tschüss', 'ich', 'du',
      'sprechen', 'zuhören', 'fragen', 'gut',
    ],
    footerLabels: ['ja', 'nein', 'mehr', 'nicht', 'bitte', 'danke'],
  },
  {
    id: 'pb-gefuehle',
    title: 'Wie geht es mir?',
    description: 'Gefühle und Befinden ausdrücken – Plauderbuch',
    icon: '😊',
    group: 'Plauderbuch',
    slotCount: 8,
    mainSlotLabels: [
      'glücklich', 'traurig', 'wütend', 'müde',
      'Angst', 'Schmerzen', 'gut', 'nicht',
    ],
    footerLabels: ['ja', 'nein', 'mehr', 'Hilfe'],
  },
  {
    id: 'pb-zuhause',
    title: 'Zuhause',
    description: 'Was passiert daheim? – Plauderbuch',
    icon: '🏡',
    group: 'Plauderbuch',
    slotCount: 12,
    mainSlotLabels: [
      'essen', 'trinken', 'schlafen', 'spielen',
      'fernsehen', 'kochen', 'Toilette', 'duschen',
      'Garten', 'Küche', 'aufstehen', 'Kleidung',
    ],
    footerLabels: ['mehr', 'nicht', 'nochmal', 'fertig', 'Hilfe', 'Pause', 'ja', 'nein'],
  },
  {
    id: 'pb-mitwem',
    title: 'Mit wem?',
    description: 'Über Personen sprechen – Plauderbuch',
    icon: '👥',
    group: 'Plauderbuch',
    slotCount: 8,
    mainSlotLabels: [
      'Mama', 'Papa', 'Freund', 'Kind',
      'Frau', 'Mann', 'ich', 'du',
    ],
    footerLabels: ['ja', 'nein', 'mehr', 'nicht', 'bitte', 'danke'],
  },
  {
    id: 'pb-wasgemacht',
    title: 'Was habe ich gemacht?',
    description: 'Aktivitäten erzählen – Plauderbuch',
    icon: '📖',
    group: 'Plauderbuch',
    slotCount: 12,
    mainSlotLabels: [
      'spielen', 'essen', 'trinken', 'schlafen',
      'spazieren', 'malen', 'lesen', 'Musik',
      'turnen', 'basteln', 'kochen', 'fernsehen',
    ],
    footerLabels: ['mehr', 'nicht', 'nochmal', 'fertig', 'Hilfe', 'Pause', 'ja', 'nein'],
  },
  {
    id: 'pb-essen',
    title: 'Essen und Trinken',
    description: 'Mahlzeiten und Getränke – Plauderbuch',
    icon: '🍽️',
    group: 'Plauderbuch',
    slotCount: 12,
    mainSlotLabels: [
      'Wasser', 'Milch', 'Saft', 'Kaffee',
      'Brot', 'Apfel', 'essen', 'trinken',
      'kochen', 'mehr', 'gut', 'nicht',
    ],
    footerLabels: ['ja', 'nein', 'mehr', 'bitte', 'danke', 'fertig'],
  },
  {
    id: 'pb-pflege',
    title: 'Pflege und Körper',
    description: 'Körperpflege und Gesundheit – Plauderbuch',
    icon: '🚿',
    group: 'Plauderbuch',
    slotCount: 8,
    mainSlotLabels: [
      'Zähne putzen', 'duschen', 'kämmen', 'Toilette',
      'Kleidung', 'Medizin', 'Windel', 'Schmerzen',
    ],
    footerLabels: ['ja', 'nein', 'fertig', 'Hilfe', 'mehr', 'nicht'],
  },
  {
    id: 'pb-unterwegs',
    title: 'Unterwegs / Wo?',
    description: 'Orte und Ausflüge – Plauderbuch',
    icon: '🚗',
    group: 'Plauderbuch',
    slotCount: 8,
    mainSlotLabels: [
      'Zuhause', 'Schule', 'draußen', 'Garten',
      'Küche', 'gehen', 'wo', 'spazieren',
    ],
    footerLabels: ['ja', 'nein', 'mehr', 'nicht', 'Hilfe', 'fertig'],
  },

  // ── Ich-Buch 2.0 ────────────────────────────────────────────
  // Seite für Seite dem Vorbild „Ich-Buch 4.0" (Konzept: Nina Fröhlich)
  // nachempfunden — siehe development/examples/Martina - Ich-Buch - 2026.pdf.
  // Personenbezogene Felder (Foto, Adresse, Sprüche) sind Platzhalter:
  // Piktogramm durch eigenes Foto ersetzen, Beschriftung anpassen.
  {
    id: 'ichbuch2-das-bin-ich',
    title: 'Das bin ich',
    description: 'Name, Geburtstag, Alter und ein großes Foto – die erste Seite jedes Ich-Buchs',
    icon: '🪪',
    group: 'Ich-Buch 2.0',
    slotCount: 4,
    mainSlotLabels: [
      'Ich heiße', 'Geburtstag',
      'So alt bin ich', 'So sehe ich aus',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-da-wohne-ich',
    title: 'Da wohne ich',
    description: 'Wohnung, Zimmer, Mitbewohner, Adresse, Telefon und mein Weg nach Hause',
    icon: '🏠',
    group: 'Ich-Buch 2.0',
    slotCount: 6,
    mainSlotLabels: [
      'Hier wohne ich', 'Das ist mein Zimmer', 'Hier wohne ich mit',
      'Meine Adresse', 'Meine Telefonnummer', 'So fahre ich nach Hause',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-meine-menschen',
    title: 'Meine Freunde und Kollegen',
    description: 'Personen-Raster mit Foto und Name – wichtige Menschen auf einen Blick',
    icon: '🧑‍🤝‍🧑',
    group: 'Ich-Buch 2.0',
    slotCount: 10,
    mainSlotLabels: [
      'Familie', 'Freund', 'Freundin', 'Kollege', 'Kollegin',
      'Betreuerin', 'Betreuer', 'Nachbar', '', '',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-das-mag-ich',
    title: 'Das mag ich',
    description: 'Lieblingsaktivitäten und Interessen – an die Person anpassen',
    icon: '❤️',
    group: 'Ich-Buch 2.0',
    slotCount: 10,
    mainSlotLabels: [
      'schwimmen', 'Entspannung', 'Musik machen', 'kochen', 'einkaufen',
      'reiten', 'tanzen', 'spazieren gehen', 'Perlen auffädeln', 'schneiden',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-das-mag-ich-nicht',
    title: 'Das mag ich nicht',
    description: 'Abneigungen und Stressauslöser – mit freien Feldern zum Ergänzen',
    icon: '🚫',
    group: 'Ich-Buch 2.0',
    slotCount: 10,
    mainSlotLabels: [
      'leise sein', 'lange sitzen', 'warten', 'weinen', 'laut',
      '', '', '', '', '',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-kommunikation',
    title: 'Kommunikation',
    description: 'So erreichst du mich – Hinweise für Kommunikationspartner',
    icon: '💬',
    group: 'Ich-Buch 2.0',
    slotCount: 6,
    mainSlotLabels: [
      'Blickkontakt aufnehmen', 'zeigen und vormachen', 'Pausen lassen und abwarten',
      'deutlich und ruhig sprechen', 'kurze klare Sätze', 'Zeit lassen',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-meine-sprueche',
    title: 'Meine Sprüche',
    description: 'Typische Aussagen der Person – Beschriftungen durch echte Zitate ersetzen',
    icon: '💭',
    group: 'Ich-Buch 2.0',
    slotCount: 6,
    mainSlotLabels: [
      'Spruch 1', 'Spruch 2', 'Spruch 3',
      'Spruch 4', 'Spruch 5', 'Spruch 6',
    ],
    footerLabels: [],
  },
  {
    id: 'ichbuch2-haushalt',
    title: 'Das kann ich im Haushalt',
    description: 'Fähigkeiten-Matrix Haushalt – Fußzeile als Legende: alleine / mit Hilfe / kann ich nicht',
    icon: '🧹',
    group: 'Ich-Buch 2.0',
    slotCount: 18,
    mainSlotLabels: [
      'Tisch decken', 'abräumen', 'abwaschen', 'abtrocknen', 'Tisch abwischen', 'Spülmaschine ausräumen',
      'fegen', 'Staub saugen', 'Boden wischen', 'Wäsche waschen', 'bügeln', 'Zimmer aufräumen',
      'einkaufen', 'kochen', 'backen', 'schneiden', 'Blumen gießen', 'Müll rausbringen',
    ],
    footerLabels: ['allein', 'mit Hilfe', 'kann ich nicht'],
  },
  {
    id: 'ichbuch2-koerperpflege',
    title: 'Das kann ich bei der Körperpflege',
    description: 'Fähigkeiten-Matrix Körperpflege – Fußzeile als Legende: alleine / mit Hilfe / kann ich nicht',
    icon: '🚿',
    group: 'Ich-Buch 2.0',
    slotCount: 18,
    mainSlotLabels: [
      'Zähne putzen', 'Gesicht waschen', 'Hände waschen', 'duschen', 'Haare waschen', 'Haare föhnen',
      'Haare kämmen', 'eincremen', 'Deo benutzen', 'schminken', 'rasieren', 'auf die Toilette gehen',
      'anziehen', 'ausziehen', 'Schuhe anziehen', 'Jacke anziehen', 'abtrocknen', 'Hygieneartikel benutzen',
    ],
    footerLabels: ['allein', 'mit Hilfe', 'kann ich nicht'],
  },
];

// ── Template-Gruppen ───────────────────────────────────────────
export interface TemplateGroup {
  id: string;
  title: string;
  description: string;
  icon: string;
  templateIds: string[];
}

export const TEMPLATE_GROUPS: TemplateGroup[] = [
  {
    id: 'basis',
    title: 'Basis',
    description: 'Grundlegende Kommunikationsseiten für den Alltag',
    icon: '📋',
    templateIds: ['ich', 'zuhause', 'pflege', 'freunde', 'arbeit', 'aktivitaeten'],
  },
  {
    id: 'ich-buch-2',
    title: 'Ich-Buch 2.0',
    description: 'Vollständiges Ich-Buch nach dem Vorbild „Ich-Buch 4.0" (Konzept: N. Fröhlich) – neun Seiten, Seite für Seite personalisierbar',
    icon: '📘',
    templateIds: [
      'ichbuch2-das-bin-ich', 'ichbuch2-da-wohne-ich', 'ichbuch2-meine-menschen',
      'ichbuch2-das-mag-ich', 'ichbuch2-das-mag-ich-nicht', 'ichbuch2-kommunikation',
      'ichbuch2-meine-sprueche', 'ichbuch2-haushalt', 'ichbuch2-koerperpflege',
    ],
  },
  {
    id: 'plauderbuch',
    title: 'Plauderbuch',
    description: 'Seiten für ein Plauderbuch – Gesprächsanlässe schaffen',
    icon: '💬',
    templateIds: [
      'pb-hallo', 'pb-gefuehle', 'pb-zuhause', 'pb-mitwem',
      'pb-wasgemacht', 'pb-essen', 'pb-pflege', 'pb-unterwegs',
    ],
  },
];
